'use client';

import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 100;

// `grecaptcha.render` (not merely the global) is the readiness signal: the global can exist before
// `render` has been installed.
function isGrecaptchaReady(): boolean {
    return typeof grecaptcha !== 'undefined' && typeof grecaptcha.render === 'function';
}

/**
 * Reports whether the reCAPTCHA SDK has finished loading, so `grecaptcha.render` can be called.
 * The SDK is injected by <RecaptchaScriptTag> and loads asynchronously, so a checkbox can mount
 * before it is ready; this seeds from the global (a checkbox mounting after a client navigation,
 * when the SDK already loaded, is ready immediately) and otherwise polls until `render` is
 * installed, then stops.
 */
export function useGrecaptchaReady(): boolean {
    const [isReady, setIsReady] = useState<boolean>(isGrecaptchaReady);

    useEffect(() => {
        if (isReady) {
            return;
        }
        const intervalId = setInterval(() => {
            if (isGrecaptchaReady()) {
                clearInterval(intervalId);
                setIsReady(true);
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [isReady]);

    return isReady;
}
